import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { getAlertStatuses, getOsmLocations } from '../../services/api.ts';

type AlertStatus = {
  id: number;
  name: string;
  is_active: boolean;
  silenced_until: string | null;
  measurement: { name: string } | null;
  alert_state: { breaching_since: string | null; last_notified: string | null } | null;
  osm_ids: number[] | null;
};

type OsmLocation = {
  id: number;
  name: string;
};

type StatusMeta = { icon: React.ReactNode; label: string; color: string };

function getStatusMeta(alert: AlertStatus): StatusMeta {
  if (!alert.is_active) return {
    icon: <CheckCircleIcon sx={{ fontSize: '16px !important', color: '#888780 !important' }} />,
    label: 'Inactive',
    color: '#888780',
  };
  const now = new Date();
  if (alert.silenced_until && new Date(alert.silenced_until) > now) return {
    icon: <PauseCircleIcon sx={{ fontSize: '16px !important', color: '#BA7517 !important' }} />,
    label: 'Silenced',
    color: '#BA7517',
  };
  if (alert.alert_state?.breaching_since) return {
    icon: <NotificationsActiveIcon sx={{ fontSize: '16px !important', color: '#993C1D !important' }} />,
    label: 'Firing',
    color: '#993C1D',
  };
  return {
    icon: <CheckCircleIcon sx={{ fontSize: '16px !important', color: '#1D9E75 !important' }} />,
    label: 'OK',
    color: '#1D9E75',
  };
}

const SEVERITY_ORDER: Record<string, number> = {
  Firing:   0,
  Silenced: 1,
  OK:       2,
  Inactive: 3,
};

function sortAlerts(alerts: AlertStatus[]): AlertStatus[] {
  return [...alerts].sort((a, b) => {
    const aLabel = getStatusMeta(a).label;
    const bLabel = getStatusMeta(b).label;
    return (SEVERITY_ORDER[aLabel] ?? 99) - (SEVERITY_ORDER[bLabel] ?? 99);
  });
}

function resolveLocation(osm_ids: number[] | null, osmMap: Map<number, string>): string {
  if (!osm_ids?.length) return 'All';
  return osm_ids.map(id => osmMap.get(id) ?? String(id)).join(', ');
}

const headCellSx = {
  fontFamily: "'Syne', sans-serif",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'var(--color-text-secondary)',
  background: 'transparent',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  py: 1.25,
};

const bodyCellSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  color: 'var(--color-text-primary)',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  py: 1,
  background: 'transparent',
};

export default function FiringAlertsTable() {
  const [alerts, setAlerts]   = React.useState<AlertStatus[]>([]);
  const [osmMap, setOsmMap]   = React.useState<Map<number, string>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  React.useEffect(() => {
    Promise.all([getAlertStatuses(), getOsmLocations()])
      .then(([alertData, osmData]: [AlertStatus[], OsmLocation[]]) => {
        setAlerts(sortAlerts(alertData));
        setOsmMap(new Map(osmData.map(o => [o.id, o.name])));
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <CircularProgress size={24} sx={{ color: '#1D9E75' }} />
    </Box>
  );

  if (error) return (
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#993C1D' }}>
      Error: {error}
    </Typography>
  );

  if (!alerts.length) return (
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--color-text-secondary)' }}>
      No alerts configured.
    </Typography>
  );

  return (
    <TableContainer sx={{ background: 'transparent', maxHeight: 300 }}>
      <Table stickyHeader size="small" aria-label="alerts table">
        <TableHead>
          <TableRow>
            <TableCell align="left"   sx={headCellSx}>Alert</TableCell>
            <TableCell align="left"   sx={headCellSx}>Location</TableCell>
            <TableCell align="center" sx={headCellSx}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alerts.map(alert => {
            const { icon, label, color } = getStatusMeta(alert);
            return (
              <TableRow key={alert.id} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell align="left" sx={{ ...bodyCellSx, fontFamily: "'Syne', sans-serif", fontWeight: 500, fontSize: 12, color: 'var(--color-text-primary)' }}>
                  {alert.name}
                </TableCell>
                <TableCell align="left" sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)' }}>
                  {resolveLocation(alert.osm_ids, osmMap)}
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color }}>
                    {icon}
                    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color }}>
                      {label}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
